import * as React from "react";
import { useCallback, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DataLake } from "@graylog/enterprise-api";
import { MenuItem } from "components/bootstrap";
import BulkActionsDropdown from "components/common/EntityDataTable/BulkActionsDropdown";
import BulkRetrievalModal from "data-lake/preview/BulkRetrievalModal";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import AddEvidenceModal from "security-app/components/Investigations/plugin/shared/AddEvidenceModal";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import useRetrieval from "data-lake/preview/hooks/useRetrieval";
import AddToInvestigationHelp from "data-lake/preview/AddToInvestigationHelp";
import useActionCondition from "security-app/components/Investigations/plugin/useActionCondition";
import { investigationDetailsQueryKey } from "security-app/hooks/queryKeys";
import useSelectableLogViewMessages from "logview/components/contexts/useSelectableLogViewMessages";
import useSelectedMessageEntities from "views/hooks/useSelectedMessageEntities";
const BulkActions = ({ retrievalPayload }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const [showRetrievalModal, setShowRetrievalModal] = useState(false);
  const sendTelemetry = useSendTelemetry("log-view-widget");
  const { isEntitySelected, selectedEntities } = useSelectedMessageEntities();
  const { selectableLogViewMessages } = useSelectableLogViewMessages();
  const selectedMessages = selectableLogViewMessages.filter(({ id, index }) => isEntitySelected(index, id));
  const addEvidenceModalRef = useRef(null);
  const queryClient = useQueryClient();
  const { bulkRetrievePayload } = useRetrieval();
  const { selectedDataLake } = useSelectedDataLake();
  const displayInvestigationActions = useActionCondition();
  const toggleShowModal = useCallback(() => {
    if (!showRetrievalModal) {
      sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].RETRIEVE_LOGS_BULK_ACTION_CLICK, {
        app_pathname: "/data-storage/preview",
        event_details: {
          messages_amount: selectedEntities.length,
          data_lake_storage_type: selectedDataLake.storageType
        }
      });
    }
    setShowRetrievalModal((cur) => !cur);
  }, [selectedDataLake.storageType, selectedDataLake.type, selectedEntities.length, sendTelemetry, showRetrievalModal]);
  const updateInvestigation = useCallback(
    (investigationIds) => DataLake.addMultipleMessagesToInvestigation({
      investigation_ids: investigationIds,
      retrieval_request: bulkRetrievePayload(
        selectedMessages.map(({ id, timestamp }) => ({ id, timestamp })),
        selectedDataLake.id,
        retrievalPayload
      )
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: investigationDetailsQueryKey() });
    }),
    [bulkRetrievePayload, queryClient, retrievalPayload, selectedDataLake.id, selectedMessages]
  );
  const onAddEvidence = async (payload) => updateInvestigation(payload.investigation_ids);
  const selectedEntitiesIds = selectedEntities.map(({ id }) => id);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BulkActionsDropdown, { bsSize: "xs" }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: toggleShowModal }, t("bulk_actions.retrieve_logs")), displayInvestigationActions && /* @__PURE__ */ React.createElement(MenuItem, { onClick: () => addEvidenceModalRef.current.toggle(), icon: "extension" }, t("bulk_actions.add_to_investigation"))), showRetrievalModal && /* @__PURE__ */ React.createElement(BulkRetrievalModal, { onCancel: toggleShowModal, retrievalPayload }), displayInvestigationActions && /* @__PURE__ */ React.createElement(
    AddEvidenceModal,
    {
      index: void 0,
      ids: selectedEntitiesIds,
      type: "logs",
      ref: addEvidenceModalRef,
      onSubmit: onAddEvidence
    },
    /* @__PURE__ */ React.createElement(AddToInvestigationHelp, null)
  ));
};
export default BulkActions;
